import axios, {AxiosRequestConfig} from 'axios'
import {getUserData} from '../modules/auth'

type AnalyticsMetadata = Record<string, string | number | boolean | null | undefined>

export type AnalyticsEventType =
  | 'page_view'
  | 'page_duration'
  | 'feature_use'
  | 'client_api_request'
  | 'client_error'
  | 'unhandled_rejection'
  | 'web_vital'
  | 'navigation_performance'
  | 'session_start'
  | 'session_end'

export interface AnalyticsEvent {
  event_uuid: string
  session_id: string
  event_type: AnalyticsEventType
  event_name?: string
  path?: string
  referrer_path?: string
  method?: string
  status_code?: number
  duration_ms?: number
  success?: boolean
  metadata?: AnalyticsMetadata
  occurred_at: string
}

type TrackedAxiosConfig = AxiosRequestConfig & {
  __analyticsStart?: number
  __skipAnalytics?: boolean
}

const SESSION_KEY = 'internal_analytics_session'
const QUEUE_KEY = 'internal_analytics_queue'
const MAX_QUEUE_SIZE = 250
const FLUSH_INTERVAL = 5000
const HEARTBEAT_INTERVAL = 30000

let queue: AnalyticsEvent[] = []
let flushTimer: number | undefined
let flushing = false
let installed = false
let sessionStarted = false
let currentPageName = ''
let currentPath = window.location.pathname
let pageStartedAt = performance.now()
let pageReferrer = ''
let largestContentfulPaint = 0
let cumulativeLayoutShift = 0

const uuid = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = (Math.random() * 16) | 0
    const value = character === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

export const getAnalyticsSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_KEY)
  if (!sessionId) {
    sessionId = uuid()
    sessionStorage.setItem(SESSION_KEY, sessionId)
  }
  return sessionId
}

const hasAuthenticatedUser = () => Boolean(getUserData()?.guid && getUserData()?.api_token)

const sanitizeText = (value: unknown, maxLength = 191) =>
  String(value || '')
    .split('')
    .filter((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength)

const normalizeApiPath = (url?: string) => {
  const raw = String(url || '')
  const path = raw.replace(/^https?:\/\/[^/]+/i, '').split('?')[0]
  return path
    .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}(?=\/|$)/gi, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .slice(0, 512)
}

const isAnalyticsRequest = (url?: string) => normalizeApiPath(url).startsWith('/analytics')

const restoreQueue = () => {
  try {
    const saved = localStorage.getItem(QUEUE_KEY)
    if (!saved) return
    const parsed = JSON.parse(saved)
    if (Array.isArray(parsed)) queue = parsed.slice(-MAX_QUEUE_SIZE)
  } catch (_error) {
    localStorage.removeItem(QUEUE_KEY)
  }
}

const persistQueue = () => {
  try {
    if (queue.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)))
    else localStorage.removeItem(QUEUE_KEY)
  } catch (_error) {
    // Analytics must never block the product experience.
  }
}

export const trackAnalyticsEvent = (
  eventType: AnalyticsEventType,
  options: Partial<Omit<AnalyticsEvent, 'event_uuid' | 'session_id' | 'event_type' | 'occurred_at'>> = {}
) => {
  if (!hasAuthenticatedUser()) return
  const event: AnalyticsEvent = {
    event_uuid: uuid(),
    session_id: getAnalyticsSessionId(),
    event_type: eventType,
    event_name: options.event_name ? sanitizeText(options.event_name) : undefined,
    path: sanitizeText(options.path || currentPath, 512),
    referrer_path: options.referrer_path ? sanitizeText(options.referrer_path, 512) : undefined,
    method: options.method ? sanitizeText(options.method, 12) : undefined,
    status_code: options.status_code,
    duration_ms:
      options.duration_ms === undefined ? undefined : Math.max(0, Math.round(options.duration_ms)),
    success: options.success,
    metadata: options.metadata,
    occurred_at: new Date().toISOString(),
  }

  queue.push(event)
  if (queue.length > MAX_QUEUE_SIZE) queue = queue.slice(-MAX_QUEUE_SIZE)
  persistQueue()
  if (queue.length >= 20) void flushAnalytics()
}

export const flushAnalytics = async () => {
  if (flushing || !queue.length || !hasAuthenticatedUser()) return
  flushing = true
  const batch = queue.splice(0, 100)
  persistQueue()

  try {
    await axios.post(
      '/analytics/events',
      {events: batch},
      {timeout: 10000, __skipAnalytics: true} as TrackedAxiosConfig
    )
  } catch (_error) {
    queue = [...batch, ...queue].slice(-MAX_QUEUE_SIZE)
    persistQueue()
  } finally {
    flushing = false
  }
}

const flushWithKeepalive = () => {
  if (!queue.length || !hasAuthenticatedUser()) return
  const user = getUserData()
  const batch = queue.splice(0, 100)
  persistQueue()
  const baseUrl = String(axios.defaults.baseURL || '')
  const url = `${baseUrl.replace(/\/$/, '')}/analytics/events`

  try {
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: user.api_token,
      },
      body: JSON.stringify({events: batch}),
    })
  } catch (_error) {
    queue = [...batch, ...queue].slice(-MAX_QUEUE_SIZE)
    persistQueue()
  }
}

export const sendAnalyticsHeartbeat = async () => {
  if (!hasAuthenticatedUser()) return
  try {
    await axios.post(
      '/analytics/heartbeat',
      {
        session_id: getAnalyticsSessionId(),
        path: currentPath,
        page_name: currentPageName,
        is_visible: document.visibilityState === 'visible',
        device_type: getDeviceType(),
        browser_name: getBrowserName(),
        metadata: {
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          language: navigator.language,
        },
      },
      {timeout: 10000, __skipAnalytics: true} as TrackedAxiosConfig
    )
  } catch (_error) {
    // Presence is best-effort and must stay invisible to the user.
  }
}

const getDeviceType = () => {
  const width = window.innerWidth
  if (width < 768) return 'Mobile'
  if (width < 1200) return 'Tablet'
  return 'Desktop'
}

const getBrowserName = () => {
  const agent = navigator.userAgent
  if (/Edg\//.test(agent)) return 'Edge'
  if (/Chrome\//.test(agent)) return 'Chrome'
  if (/Firefox\//.test(agent)) return 'Firefox'
  if (/Safari\//.test(agent)) return 'Safari'
  return 'Outro'
}

const routeLabels: Record<string, string> = {
  '/': 'Inicio',
  '/home': 'Inicio',
  '/users': 'Usuarios',
  '/companys': 'Empresas',
  '/lojas': 'Lojas',
  '/listas': 'Listas',
  '/bus': 'Business Units',
  '/regionais': 'Regionais',
  '/tamanhos': 'Tamanhos',
  '/precos': 'Precos',
  '/cupons': 'Cupons',
  '/apuracao': 'Apuracao',
  '/logs': 'Logs',
  '/alerts': 'Alertas',
  '/permissions': 'Permissoes',
  '/settings': 'Configuracoes',
  '/support': 'Suporte',
  '/nps': 'NPS',
  '/campaigns': 'Simulador de campanhas',
  '/ai': 'Compra AI',
  '/agent-studio': 'Agent Studio',
  '/analytics': 'Analytics',
}

export const getAnalyticsPageName = (pathname: string) => {
  if (routeLabels[pathname]) return routeLabels[pathname]
  const exactPrefix = Object.keys(routeLabels)
    .filter((route) => route !== '/' && pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0]
  if (exactPrefix) return routeLabels[exactPrefix]

  return pathname
    .split('/')
    .filter(Boolean)
    .map((part) =>
      part
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase())
    )
    .join(' / ') || 'Inicio'
}

export const trackPageChange = (pathname: string, pageName?: string) => {
  const nextPath = pathname.split('?')[0] || '/'
  const now = performance.now()
  if (!sessionStarted && hasAuthenticatedUser()) {
    sessionStarted = true
    trackAnalyticsEvent('session_start', {
      event_name: 'Session start',
      metadata: {
        device_type: getDeviceType(),
        browser_name: getBrowserName(),
      },
    })
  }

  if (currentPath && currentPageName) {
    trackAnalyticsEvent('page_duration', {
      event_name: currentPageName,
      path: currentPath,
      duration_ms: now - pageStartedAt,
      metadata: {visible: document.visibilityState === 'visible'},
    })
  }

  pageReferrer = currentPath
  currentPath = nextPath
  currentPageName = pageName || getAnalyticsPageName(nextPath)
  pageStartedAt = now

  trackAnalyticsEvent('page_view', {
    event_name: currentPageName,
    path: currentPath,
    referrer_path: pageReferrer,
    metadata: {
      device_type: getDeviceType(),
      browser_name: getBrowserName(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
    },
  })
  void sendAnalyticsHeartbeat()
}

const featureNameFromElement = (element: Element) => {
  const explicit = element.getAttribute('data-analytics-feature')
  if (explicit) return explicit

  const ariaLabel = element.getAttribute('aria-label')
  const title = element.getAttribute('title')
  const text = (element.textContent || '').replace(/\s+/g, ' ').trim()
  return sanitizeText(ariaLabel || title || text, 100)
}

const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof Element)) return
  const element = target.closest('[data-analytics-feature], button, a, [role="button"]')
  if (!element || element.hasAttribute('data-analytics-ignore')) return

  const featureName = featureNameFromElement(element)
  if (!featureName || featureName.length < 2) return

  trackAnalyticsEvent('feature_use', {
    event_name: featureName,
    metadata: {
      element_type: element.tagName.toLowerCase(),
      element_id: sanitizeText(element.id, 80) || null,
    },
  })
}

const recordNavigationPerformance = () => {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  if (!navigation) return
  trackAnalyticsEvent('navigation_performance', {
    event_name: 'Page load',
    duration_ms: navigation.duration,
    metadata: {
      ttfb_ms: Math.round(navigation.responseStart),
      dom_interactive_ms: Math.round(navigation.domInteractive),
      dom_content_loaded_ms: Math.round(navigation.domContentLoadedEventEnd),
      load_ms: Math.round(navigation.loadEventEnd),
    },
  })

  const firstPaint = performance.getEntriesByName('first-contentful-paint')[0]
  if (firstPaint) {
    trackAnalyticsEvent('web_vital', {
      event_name: 'FCP',
      duration_ms: firstPaint.startTime,
    })
  }
}

const installPerformanceObservers = () => {
  if (!('PerformanceObserver' in window)) return
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lastEntry = entries[entries.length - 1]
      if (lastEntry) largestContentfulPaint = lastEntry.startTime
    })
    lcpObserver.observe({type: 'largest-contentful-paint', buffered: true} as PerformanceObserverInit)

    const layoutObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) cumulativeLayoutShift += entry.value
      })
    })
    layoutObserver.observe({type: 'layout-shift', buffered: true} as PerformanceObserverInit)
  } catch (_error) {
    // Older browsers can reject unsupported entry types.
  }
}

const recordFinalVitals = () => {
  if (largestContentfulPaint > 0) {
    trackAnalyticsEvent('web_vital', {
      event_name: 'LCP',
      duration_ms: largestContentfulPaint,
    })
    largestContentfulPaint = 0
  }
  if (cumulativeLayoutShift > 0) {
    trackAnalyticsEvent('web_vital', {
      event_name: 'CLS x1000',
      duration_ms: cumulativeLayoutShift * 1000,
    })
    cumulativeLayoutShift = 0
  }
}

const installAxiosTracking = () => {
  axios.interceptors.request.use((config: TrackedAxiosConfig) => {
    if (!config.__skipAnalytics && !isAnalyticsRequest(config.url)) {
      config.__analyticsStart = performance.now()
    }
    return config
  })

  axios.interceptors.response.use(
    (response) => {
      const config = response.config as TrackedAxiosConfig
      if (!config.__skipAnalytics && !isAnalyticsRequest(config.url) && config.__analyticsStart) {
        trackAnalyticsEvent('client_api_request', {
          event_name: `${String(config.method || 'GET').toUpperCase()} ${normalizeApiPath(config.url)}`,
          path: normalizeApiPath(config.url),
          method: String(config.method || 'GET').toUpperCase(),
          status_code: response.status,
          duration_ms: performance.now() - config.__analyticsStart,
          success: response.status < 400,
        })
      }
      return response
    },
    (error) => {
      const config = (error.config || {}) as TrackedAxiosConfig
      if (!config.__skipAnalytics && !isAnalyticsRequest(config.url) && config.__analyticsStart) {
        trackAnalyticsEvent('client_api_request', {
          event_name: `${String(config.method || 'GET').toUpperCase()} ${normalizeApiPath(config.url)}`,
          path: normalizeApiPath(config.url),
          method: String(config.method || 'GET').toUpperCase(),
          status_code: error.response?.status || 0,
          duration_ms: performance.now() - config.__analyticsStart,
          success: false,
          metadata: {network_error: !error.response},
        })
      }
      return Promise.reject(error)
    }
  )
}

export const installGlobalAnalytics = () => {
  if (installed) return
  installed = true
  restoreQueue()
  installAxiosTracking()
  installPerformanceObservers()
  document.addEventListener('click', handleDocumentClick, true)

  window.addEventListener('error', (event) => {
    trackAnalyticsEvent('client_error', {
      event_name: sanitizeText(event.message || 'Erro de JavaScript', 180),
      metadata: {
        file: sanitizeText(event.filename, 200),
        line: event.lineno || null,
        column: event.colno || null,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason || 'Promise rejeitada')
    trackAnalyticsEvent('unhandled_rejection', {event_name: sanitizeText(reason, 180)})
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      recordFinalVitals()
      void flushAnalytics()
    }
    void sendAnalyticsHeartbeat()
  })

  window.addEventListener('pagehide', () => {
    trackAnalyticsEvent('page_duration', {
      event_name: currentPageName,
      path: currentPath,
      duration_ms: performance.now() - pageStartedAt,
    })
    trackAnalyticsEvent('session_end', {
      event_name: 'Session end',
      duration_ms: performance.now(),
    })
    recordFinalVitals()
    flushWithKeepalive()
  })

  window.addEventListener('load', () => window.setTimeout(recordNavigationPerformance, 0))

  if (hasAuthenticatedUser()) {
    sessionStarted = true
    trackAnalyticsEvent('session_start', {
      event_name: 'Session start',
      metadata: {
        device_type: getDeviceType(),
        browser_name: getBrowserName(),
      },
    })
  }

  flushTimer = window.setInterval(() => void flushAnalytics(), FLUSH_INTERVAL)
  window.setInterval(() => void sendAnalyticsHeartbeat(), HEARTBEAT_INTERVAL)
}

export const stopGlobalAnalytics = () => {
  if (flushTimer) window.clearInterval(flushTimer)
  flushTimer = undefined
}
